import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Collapse,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NightsStay as NightsStayIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

interface ContactUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  position?: string;
  workPhone?: string;
  phoneNumber?: string;
  role?: string; // Optional - deprecated field, kept for backward compatibility
  notWorkingToday?: boolean;
  todayStatus?: string | null;
}

const TODAY_STATUS_I18N: Record<string, string> = {
  day_off: 'schedule.dayOff',
  mothers_day: 'schedule.mothersDay',
  sick_leave: 'schedule.sickLeave',
  rest_day: 'schedule.restDay',
  state_holiday: 'schedule.stateHoliday',
  business_trip: 'schedule.businessTrip',
  military_service: 'schedule.militaryService',
  praktika: 'schedule.praktika'
};

const notWorkingChipSx = {
  bgcolor: 'rgba(237, 233, 254, 0.92)',
  borderColor: '#a78bfa',
  color: '#5b21b6',
  fontWeight: 700,
  letterSpacing: '0.02em',
  boxShadow: 'inset 0 0 10px rgba(196, 181, 253, 0.7), 0 0 10px rgba(167, 139, 250, 0.35)',
  '& .MuiChip-icon': { color: '#7c3aed' }
};

const notWorkingChipDarkSx = {
  ...notWorkingChipSx,
  bgcolor: 'rgba(76, 29, 149, 0.55)',
  borderColor: '#c4b5fd',
  color: '#ede9fe',
  boxShadow: 'inset 0 0 10px rgba(167, 139, 250, 0.45), 0 0 12px rgba(124, 58, 237, 0.4)',
  '& .MuiChip-icon': { color: '#ddd6fe' }
};

const notWorkingCardSx = {
  height: '100%',
  position: 'relative' as const,
  overflow: 'hidden',
  border: '1px solid rgba(167, 139, 250, 0.55)',
  backgroundImage: `
    radial-gradient(ellipse 80% 50% at 10% 0%, rgba(237, 233, 254, 0.95) 0%, transparent 55%),
    radial-gradient(ellipse 50% 40% at 95% 20%, rgba(196, 181, 253, 0.45) 0%, transparent 50%),
    linear-gradient(165deg, #f5f3ff 0%, #ede9fe 48%, #ddd6fe 100%)
  `,
  boxShadow: 'inset 0 0 18px rgba(196, 181, 253, 0.35), 0 0 14px rgba(139, 92, 246, 0.12)',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: 'linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.45) 50%, transparent 60%)',
    backgroundSize: '220% 100%',
    animation: 'nedirbaShimmer 8s ease-in-out infinite',
    opacity: 0.7
  },
  '@keyframes nedirbaShimmer': {
    '0%, 100%': { backgroundPosition: '0% 0%' },
    '50%': { backgroundPosition: '100% 0%' }
  }
};

const notWorkingCardDarkSx = {
  ...notWorkingCardSx,
  border: '1px solid rgba(196, 181, 253, 0.4)',
  backgroundImage: `
    radial-gradient(ellipse 80% 50% at 10% 0%, rgba(139, 92, 246, 0.28) 0%, transparent 55%),
    radial-gradient(ellipse 50% 40% at 95% 20%, rgba(76, 29, 149, 0.45) 0%, transparent 50%),
    linear-gradient(165deg, #2e1065 0%, #4c1d95 48%, #5b21b6 100%)
  `,
  boxShadow: 'inset 0 0 22px rgba(91, 33, 182, 0.45), 0 0 16px rgba(167, 139, 250, 0.25)'
};

interface GroupedContacts {
  [department: string]: ContactUser[];
}

interface PartnerContact {
  id: string;
  companyOrName: string;
  phoneNumber: string;
}

const Kontaktai: React.FC = () => {
  const { t } = useTranslation();
  const { token, hasPermission } = useAuth();
  const [contacts, setContacts] = useState<ContactUser[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<ContactUser[]>([]);
  const [groupedContacts, setGroupedContacts] = useState<GroupedContacts>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedDepartments, setExpandedDepartments] = useState<{ [key: string]: boolean }>({});
  const [tabValue, setTabValue] = useState(0);
  const [partners, setPartners] = useState<PartnerContact[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersSearchTerm, setPartnersSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<PartnerContact | null>(null);
  const [addForm, setAddForm] = useState({ companyOrName: '', phoneNumber: '' });
  const [addSubmitting, setAddSubmitting] = useState(false);

  const canViewPartners = hasPermission('kontaktai.partners.view');
  const canManagePartners = hasPermission('kontaktai.partners.manage');

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (canViewPartners && tabValue === 1) {
      fetchPartners();
    }
  }, [canViewPartners, tabValue]);

  const fetchPartners = async () => {
    if (!token) return;
    try {
      setPartnersLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.CONTACTS_PARTNERS), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || []);
      }
    } catch (error) {
      console.error('Error fetching partner contacts:', error);
    } finally {
      setPartnersLoading(false);
    }
  };

  useEffect(() => {
    filterContacts();
  }, [searchTerm, contacts]);

  useEffect(() => {
    groupContactsByDepartment();
  }, [filteredContacts]);

  const fetchContacts = async () => {
    try {
      if (!token) return;

      const response = await fetch(getApiUrl(API_ENDPOINTS.CONTACTS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const normalizePhone = (s: string) => (s || '').replace(/\D/g, '');

  const filterContacts = () => {
    if (!searchTerm.trim()) {
      setFilteredContacts(contacts);
      return;
    }

    const term = searchTerm.trim().toLowerCase();
    const termDigits = normalizePhone(searchTerm);

    const filtered = contacts.filter(contact => {
      if (
        contact.firstName.toLowerCase().includes(term) ||
        contact.lastName.toLowerCase().includes(term) ||
        contact.department?.toLowerCase().includes(term) ||
        contact.position?.toLowerCase().includes(term) ||
        contact.email?.toLowerCase().includes(term)
      ) {
        return true;
      }
      if (termDigits && (contact.workPhone || contact.phoneNumber)) {
        const workDigits = normalizePhone(contact.workPhone || '');
        const personalDigits = normalizePhone(contact.phoneNumber || '');
        if (workDigits.includes(termDigits) || personalDigits.includes(termDigits)) {
          return true;
        }
      }
      return false;
    });
    setFilteredContacts(filtered);
  };

  const groupContactsByDepartment = () => {
    const grouped: GroupedContacts = {};
    
    filteredContacts.forEach(contact => {
      const department = contact.department || 'No Department';
      if (!grouped[department]) {
        grouped[department] = [];
      }
      grouped[department].push(contact);
    });

    // Sort departments alphabetically
    const sortedGrouped: GroupedContacts = {};
    Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'No Department') return 1;
        if (b === 'No Department') return -1;
        return a.localeCompare(b);
      })
      .forEach(key => {
        sortedGrouped[key] = grouped[key];
      });

    setGroupedContacts(sortedGrouped);

    setExpandedDepartments((prev) => {
      const next = { ...prev };
      if (searchTerm.trim()) {
        Object.keys(sortedGrouped).forEach((dept) => {
          next[dept] = true;
        });
        return next;
      }
      Object.entries(sortedGrouped).forEach(([dept, people]) => {
        if (people.some((c) => c.notWorkingToday)) {
          next[dept] = true;
        }
      });
      return next;
    });
  };

  const toggleDepartment = (department: string) => {
    setExpandedDepartments(prev => ({
      ...prev,
      [department]: !prev[department]
    }));
  };

  const filteredPartners = partners.filter(p => {
    if (!partnersSearchTerm.trim()) return true;
    const term = partnersSearchTerm.trim().toLowerCase();
    const termDigits = (partnersSearchTerm || '').replace(/\D/g, '');
    if (p.companyOrName.toLowerCase().includes(term)) return true;
    if (termDigits && (p.phoneNumber || '').replace(/\D/g, '').includes(termDigits)) return true;
    return false;
  });

  const handleAddPartner = () => {
    setAddForm({ companyOrName: '', phoneNumber: '' });
    setAddDialogOpen(true);
  };

  const handleAddSubmit = async () => {
    if (!addForm.companyOrName.trim() || !addForm.phoneNumber.trim()) return;
    if (!token) return;
    setAddSubmitting(true);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.CONTACTS_PARTNERS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addForm)
      });
      if (response.ok) {
        setAddDialogOpen(false);
        fetchPartners();
      } else {
        const data = await response.json();
        console.error('Add partner failed:', data);
      }
    } catch (error) {
      console.error('Error adding partner:', error);
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDeleteClick = (partner: PartnerContact) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!partnerToDelete || !token) return;
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.CONTACT_PARTNER(partnerToDelete.id)), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDeleteDialogOpen(false);
        setPartnerToDelete(null);
        fetchPartners();
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'employee': return 'primary';
      default: return 'default';
    }
  };

  const renderContactCard = (contact: ContactUser) => {
    const notWorking = !!contact.notWorkingToday;
    const statusKey = contact.todayStatus ? TODAY_STATUS_I18N[contact.todayStatus] : null;
    const statusLabel = statusKey
      ? t(statusKey)
      : t('kontaktai.notWorkingToday', 'Šiandien nedirba');

    return (
    <Card
      key={contact.id}
      sx={(theme) =>
        notWorking
          ? theme.palette.mode === 'dark'
            ? notWorkingCardDarkSx
            : notWorkingCardSx
          : { height: '100%' }
      }
    >
      <CardContent sx={notWorking ? { position: 'relative', zIndex: 1 } : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              fontSize: '1.5rem',
              bgcolor: notWorking ? '#7c3aed' : 'primary.main',
              mr: 2
            }}
          >
            {contact.firstName?.[0]}{contact.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {contact.firstName} {contact.lastName}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 0.5 }}>
              <Chip
                label={contact.position?.trim() || t('users.employee')}
                color={getRoleColor(contact.role || 'employee') as any}
                size="small"
              />
              {notWorking && (
                <Tooltip title={statusLabel}>
                  <Chip
                    size="small"
                    icon={<NightsStayIcon />}
                    label={t('kontaktai.notWorkingToday', 'Šiandien nedirba')}
                    variant="outlined"
                    sx={(theme) =>
                      theme.palette.mode === 'dark' ? notWorkingChipDarkSx : notWorkingChipSx
                    }
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {contact.email}
              </Typography>
              <Chip 
                label="Always visible" 
                size="small" 
                color="success" 
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          )}

          {contact.workPhone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {contact.workPhone}
              </Typography>
              <Chip 
                label="Always visible" 
                size="small" 
                color="success" 
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          )}

          {contact.phoneNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {contact.phoneNumber}
              </Typography>
              <Chip 
                label="Shared by user" 
                size="small" 
                color="info" 
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
    );
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          {t('kontaktai.title', 'Contacts')}
        </Typography>
        <Typography>{t('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('kontaktai.title', 'Contacts')}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold', fontSize: '1.5rem', textDecoration: 'underline', color: '#b71c1c' }}>
        {t('kontaktai.subtitle')}
      </Typography>

      {canViewPartners && (
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
          <Tab label={t('kontaktai.tabEmployees', 'Employees')} icon={<WorkIcon />} iconPosition="start" />
          <Tab label={t('kontaktai.tabPartners', 'Partneriai')} icon={<BusinessIcon />} iconPosition="start" />
        </Tabs>
      )}

      {tabValue === 0 && (
        <>
          <Box sx={{ 
            backgroundColor: 'info.light', 
            p: 2, 
            borderRadius: 1, 
            mb: 3,
            border: '1px solid',
            borderColor: 'info.main'
          }}>
            <Typography variant="body2" color="info.contrastText" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" />
              {t('kontaktai.emailAlwaysVisible')}
            </Typography>
            <Typography variant="body2" color="info.contrastText" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <PhoneIcon fontSize="small" />
              {t('kontaktai.personalPhoneShared')}
            </Typography>
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('kontaktai.searchPlaceholder', 'Search contacts...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {Object.keys(groupedContacts).length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center">
                  {searchTerm ? t('kontaktai.noResults', 'No contacts found matching your search') : t('kontaktai.noContacts', 'No contacts available')}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(groupedContacts).map(([department, departmentContacts]) => (
                <Card key={department} sx={{ overflow: 'visible' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        backgroundColor: 'grey.50',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { backgroundColor: 'grey.100' }
                      }}
                      onClick={() => toggleDepartment(department)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WorkIcon color="primary" />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {department === 'No Department' ? t('kontaktai.noDepartment') : t(`common.departments.${department}`, department)}
                          </Typography>
                          <Chip 
                            label={`${departmentContacts.length} ${departmentContacts.length === 1 ? t('kontaktai.contact') : t('kontaktai.contacts')}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {departmentContacts.some((c) => c.notWorkingToday) && (
                            <Chip
                              size="small"
                              icon={<NightsStayIcon />}
                              label={t('kontaktai.notWorkingToday', 'Šiandien nedirba')}
                              variant="outlined"
                              sx={(theme) =>
                                theme.palette.mode === 'dark' ? notWorkingChipDarkSx : notWorkingChipSx
                              }
                            />
                          )}
                        </Box>
                        {expandedDepartments[department] ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
                      </Box>
                    </Box>
                    <Collapse in={expandedDepartments[department]}>
                      <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                          {departmentContacts.map(renderContactCard)}
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      {tabValue === 1 && canViewPartners && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t('kontaktai.partners.searchPlaceholder', 'Search partners...')}
              value={partnersSearchTerm}
              onChange={(e) => setPartnersSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            />
            {canManagePartners && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddPartner}>
                {t('kontaktai.partners.add', 'Add')}
              </Button>
            )}
          </Box>

          {partnersLoading ? (
            <Typography>{t('common.loading', 'Loading...')}</Typography>
          ) : filteredPartners.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center">
                  {partnersSearchTerm ? t('kontaktai.partners.noResults', 'No partners found') : t('kontaktai.partners.noPartners', 'No partner contacts')}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
              {filteredPartners.map((partner) => (
                <Card key={partner.id} sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {partner.companyOrName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{partner.phoneNumber}</Typography>
                        </Box>
                      </Box>
                      {canManagePartners && (
                        <Tooltip title={t('kontaktai.partners.delete', 'Delete')}>
                          <IconButton size="small" onClick={() => handleDeleteClick(partner)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('kontaktai.partners.add', 'Add')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('kontaktai.partners.companyName', 'Company / Name')}
            value={addForm.companyOrName}
            onChange={(e) => setAddForm(f => ({ ...f, companyOrName: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('kontaktai.partners.phone', 'Phone number')}
            value={addForm.phoneNumber}
            onChange={(e) => setAddForm(f => ({ ...f, phoneNumber: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={addSubmitting || !addForm.companyOrName.trim() || !addForm.phoneNumber.trim()}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('kontaktai.partners.delete', 'Delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('kontaktai.partners.deleteConfirm', 'Are you sure you want to delete this contact?')} {partnerToDelete?.companyOrName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Kontaktai;
